// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {fetchGuildBoostCount} from '@app/features/boost/commands/GuildBoostCommands';
import {openGuildBoostPanel} from '@app/features/boost/commands/GuildBoostPanelCommands';
import styles from '@app/features/boost/components/GuildBoostGoalRow.module.css';
import {GuildBoostLevelBadge} from '@app/features/boost/components/GuildBoostLevelBadge';
import {
	GUILD_BOOST_LEVELS,
	getGuildBoostLevelCards,
	isGuildBoostAvailable,
} from '@app/features/boost/utils/GuildBoostLevels';
import {
	BOOST_COUNT_DESCRIPTOR,
	BOOST_DESCRIPTOR,
	FULL_POWER_DESCRIPTOR,
	GOAL_DESCRIPTOR,
	guildBoostPerkLabel,
	LEVEL_PROGRESS_DESCRIPTOR,
	LEVEL_TOTAL_DESCRIPTOR,
	NEXT_REWARDS_DESCRIPTOR,
	PROGRESS_DESCRIPTOR,
	REMAINING_BOOSTS_DESCRIPTOR,
	TOP_LEVEL_DESCRIPTOR,
} from '@app/features/boost/utils/GuildBoostMessages';
import Guilds from '@app/features/guild/state/Guilds';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {useLingui} from '@lingui/react/macro';
import {CaretRightIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useRef, useState} from 'react';

const STEPS = GUILD_BOOST_LEVELS.length;
// Count-only changes have no gateway event, so the next segment fills only from a count read on hover.
function segmentFill(target: (typeof GUILD_BOOST_LEVELS)[number], level: number, count: number | null): number {
	if (target.level <= level) return 1;
	if (target.level !== level + 1 || count === null) return 0;
	const from = GUILD_BOOST_LEVELS[level - 1]?.boosts ?? 0;
	return Math.min(1, Math.max(0, (count - from) / (target.boosts - from)));
}

export const GuildBoostGoalRow = observer(({guildId, onBanner = false}: {guildId: string; onBanner?: boolean}) => {
	const {i18n} = useLingui();
	const [animate, setAnimate] = useState(() => !Accessibility.useReducedMotion);
	const [live, setLive] = useState<{guildId: string; count: number} | null>(null);
	const reading = useRef<string | null>(null);
	const [celebrate, setCelebrate] = useState(false);
	const guild = Guilds.getGuild(guildId);
	const level = guild?.premiumTier ?? 0;
	const seen = useRef({guildId, level});
	useEffect(() => {
		const previous = seen.current;
		seen.current = {guildId, level};
		if (previous.guildId === guildId && level > previous.level && !Accessibility.useReducedMotion) setCelebrate(true);
	}, [guildId, level]);
	if (!guild || !isGuildBoostAvailable(guild)) return null;
	const next = GUILD_BOOST_LEVELS.find((candidate) => candidate.level === level + 1);
	const count = live?.guildId === guildId ? live.count : null;
	const readCount = () => {
		if (count !== null || reading.current === guildId) return;
		reading.current = guildId;
		fetchGuildBoostCount(guildId)
			.then((counts) => setLive({guildId, count: counts.premiumSubscriptionCount ?? 0}))
			.catch(() => {})
			.finally(() => {
				if (reading.current === guildId) reading.current = null;
			});
	};
	const label = next ? i18n._(GOAL_DESCRIPTOR, {level: next.level}) : i18n._(FULL_POWER_DESCRIPTOR);
	const progressLabel = i18n._(LEVEL_PROGRESS_DESCRIPTOR, {level});
	const rewards = next
		? (getGuildBoostLevelCards(RuntimeConfig.limits)
				.find((candidate) => candidate.level === next.level)
				?.perks.filter((perk) => perk.upgraded) ?? [])
		: [];
	const tooltip = next ? (
		<div className={styles.tooltip}>
			{count === null ? (
				<span className={styles.tooltipLead}>
					{i18n._(LEVEL_TOTAL_DESCRIPTOR, {count: next.boosts, level: next.level})}
				</span>
			) : (
				<>
					<span className={styles.tooltipLead}>{i18n._(PROGRESS_DESCRIPTOR, {count, goal: next.boosts})}</span>
					<span>
						{i18n._(REMAINING_BOOSTS_DESCRIPTOR, {count: Math.max(0, next.boosts - count), level: next.level})}
					</span>
				</>
			)}
			<span className={styles.tooltipHeading}>{i18n._(NEXT_REWARDS_DESCRIPTOR, {level: next.level})}</span>
			<ul className={styles.tooltipPerks}>
				{rewards.map((perk) => (
					<li key={perk.type}>{guildBoostPerkLabel(i18n, perk)}</li>
				))}
			</ul>
		</div>
	) : (
		<div className={styles.tooltip}>
			<span className={styles.tooltipLead}>{i18n._(TOP_LEVEL_DESCRIPTOR)}</span>
			{count !== null && <span>{i18n._(BOOST_COUNT_DESCRIPTOR, {count})}</span>}
		</div>
	);
	return (
		<Tooltip text={() => tooltip} position="bottom">
			<button
				type="button"
				className={styles.row}
				data-on-banner={onBanner}
				data-level={level}
				data-celebrate={celebrate}
				aria-label={`${label}, ${progressLabel}`}
				aria-haspopup="dialog"
				onClick={() => openGuildBoostPanel(guildId)}
				onPointerOver={readCount}
				onFocus={readCount}
				onAnimationEnd={(event) => {
					if (event.target === event.currentTarget) setCelebrate(false);
				}}
				data-flx="boost.goal-row.button"
			>
				<span className={styles.head}>
					<span className={styles.badge}>
						<GuildBoostLevelBadge level={next?.level ?? level} size={20} />
					</span>
					<span className={styles.goal}>{label}</span>
					<span className={styles.cta} data-flx="boost.goal-row.cta">
						<span className={styles.ctaLabel}>{i18n._(BOOST_DESCRIPTOR)}</span>
						<CaretRightIcon className={styles.ctaIcon} weight="bold" aria-hidden="true" />
					</span>
				</span>
				<span
					className={styles.track}
					role="progressbar"
					aria-label={progressLabel}
					aria-valuemin={0}
					aria-valuenow={level}
					aria-valuemax={STEPS}
				>
					{GUILD_BOOST_LEVELS.map((candidate) => (
						<span
							key={candidate.level}
							className={styles.segment}
							data-state={candidate.level <= level ? 'unlocked' : candidate.level === next?.level ? 'next' : 'locked'}
							style={
								{'--segment-color': `var(--boost-level-${Math.max(candidate.level, level)})`} as React.CSSProperties
							}
							data-flx="boost.goal-row.segment"
						>
							<span
								className={styles.fill}
								style={{'--fill': segmentFill(candidate, level, count)} as React.CSSProperties}
								data-animate={animate && !Accessibility.useReducedMotion}
								onAnimationEnd={(event) => {
									event.stopPropagation();
									setAnimate(false);
								}}
								data-flx="boost.goal-row.fill"
							/>
						</span>
					))}
				</span>
			</button>
		</Tooltip>
	);
});
